import React from 'react';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  Text,
  Badge,
  Button,
  VStack,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import { FaBell } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../hooks/useNotifications';
import { formatDateDMY } from '../utils/formatDate';

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unread = data?.unreadCount ?? 0;
  const notifications = data?.notifications ?? [];

  const handleClick = (id: string, link?: string) => {
    markRead.mutate(id);
    if (link) navigate(link);
  };

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={IconButton}
        aria-label="Notifications"
        icon={<FaBell />}
        variant="ghost"
        size="lg"
        position="relative"
      >
        {unread > 0 && (
          <Badge
            colorScheme="red"
            borderRadius="full"
            position="absolute"
            top={1}
            right={1}
            fontSize="xs"
          >
            {unread > 9 ? '9+' : unread}
          </Badge>
        )}
      </MenuButton>
      <MenuList maxW="360px" p={0} maxH="400px" overflowY="auto">
        <HStack px={4} py={3} justify="space-between" borderBottomWidth={1}>
          <Text fontWeight="bold">Notifications</Text>
          {unread > 0 && (
            <Button size="xs" variant="link" onClick={() => markAllRead.mutate()}>
              Mark all read
            </Button>
          )}
        </HStack>
        {isLoading ? (
          <Box p={4} textAlign="center">
            <Spinner size="sm" />
          </Box>
        ) : notifications.length === 0 ? (
          <Box p={4}>
            <Text fontSize="sm" color="gray.500">
              No notifications yet
            </Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={0}>
            {notifications.map((n) => (
              <MenuItem
                key={n._id}
                py={3}
                bg={n.read ? undefined : 'blue.50'}
                onClick={() => handleClick(n._id, n.link)}
              >
                <Box w="full">
                  <Text fontSize="sm" fontWeight="semibold">
                    {n.title}
                  </Text>
                  <Text fontSize="xs" color="gray.600" noOfLines={2}>
                    {n.message}
                  </Text>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    {formatDateDMY(n.createdAt)}
                  </Text>
                </Box>
              </MenuItem>
            ))}
          </VStack>
        )}
      </MenuList>
    </Menu>
  );
};

export default NotificationBell;
